/**
 * Componente BackupCodesDisplay
 * Il Diario di Bordo - SaaS
 *
 * Visualizzazione e rigenerazione codici di backup.
 */

import { useState } from 'react'
import { useTwoFactor } from '../../hooks/useTwoFactor'
import { 
  Key, 
  Copy, 
  Check, 
  Download, 
  RefreshCw, 
  Loader2, 
  AlertCircle,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react'

export default function BackupCodesDisplay({ 
  status, 
  onRegenerate,
  showGenerateOnly = false 
}) {
  const [showCodes, setShowCodes] = useState(false)
  const [codes, setCodes] = useState(null)
  const [password, setPassword] = useState('')
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  
  const { generateBackupCodes, downloadBackupCodesPdf, loading, error, clearError } = useTwoFactor()

  const handleGenerate = async () => {
    // Se esistono già codici, richiedi password
    if (status?.configured && !password) {
      setShowPasswordPrompt(true)
      return
    }

    try {
      clearError()
      const data = await generateBackupCodes(password || null)
      
      // Verifica che i codici siano presenti nella risposta
      if (data && data.codes && Array.isArray(data.codes)) {
        setCodes(data.codes)
        setShowCodes(true)
        setPassword('')
        setShowPasswordPrompt(false)
        // NON chiamare onRegenerate qui - lo facciamo quando l'utente clicca "Ho salvato i codici"
      } else {
        console.error('Risposta inattesa dal server:', data)
      }
    } catch (err) {
      console.error('Errore generazione codici:', err)
      if (err.code === 'PASSWORD_REQUIRED') {
        setShowPasswordPrompt(true)
      }
      // L'errore rimane visibile tramite lo stato 'error' dell'hook
    }
  }

  const handleCopyAll = async () => {
    if (!codes) return
    
    try {
      const text = codes.join('\n')
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Errore copia:', err)
    }
  }

  const handleDownload = async () => {
    if (!codes) return
    
    try {
      setDownloadingPdf(true)
      await downloadBackupCodesPdf(codes)
    } catch (err) {
      console.error('Errore download PDF:', err)
    } finally {
      setDownloadingPdf(false)
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Password prompt
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (showPasswordPrompt) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-200">
                Attenzione
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Generare nuovi codici invaliderà tutti i codici precedenti. 
                Inserisci la tua password per confermare.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="Inserisci la tua password"
            autoFocus
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => {
              setShowPasswordPrompt(false)
              setPassword('')
              clearError()
            }}
            className="flex-1 btn-secondary"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!password || loading}
            className="flex-1 btn-primary disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generazione...
              </>
            ) : (
              'Genera nuovi codici'
            )}
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Nuovi codici generati
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (codes && showCodes) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
            ⚠️ Salva questi codici
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            Questi codici non verranno più mostrati. Salvali in un luogo sicuro.
          </p>
          
          <div className="grid grid-cols-2 gap-2 mb-4">
            {codes.map((code, i) => (
              <code
                key={i}
                className="px-3 py-2 bg-white dark:bg-gray-800 rounded text-sm font-mono text-center"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleCopyAll}
              className="btn-secondary flex-1 text-sm py-1.5"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copiati!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copia tutti
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloadingPdf}
              className="btn-secondary flex-1 text-sm py-1.5 disabled:opacity-50"
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Download...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Scarica PDF
                </>
              )}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setCodes(null)
            setShowCodes(false)
            // Aggiorna lo status solo dopo che l'utente ha salvato i codici
            onRegenerate?.()
          }}
          className="w-full btn-primary"
        >
          Ho salvato i codici
        </button>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Status display
  // ═══════════════════════════════════════════════════════════════════════════

  if (showGenerateOnly || !status?.configured) {
    return (
      <div className="space-y-4">
        <div className="text-center py-4">
          <Key className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {status?.configured 
              ? 'I codici di backup ti permettono di accedere se perdi il dispositivo.'
              : 'Genera codici di backup per accedere in caso di emergenza.'}
          </p>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generazione...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Genera codici di backup
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center">
          <Key className="h-5 w-5 text-gray-400 mr-3" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              Codici di backup
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {status.remaining} di {status.total} codici disponibili
            </p>
          </div>
        </div>
        
        {status.needsRegeneration && (
          <span className="px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 rounded">
            Pochi codici
          </span>
        )}
      </div>

      {/* Warning */}
      {status.needsRegeneration && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Hai solo {status.remaining} codici rimanenti. 
              Ti consigliamo di generarne di nuovi.
            </p>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
        {status.generatedAt && (
          <p>
            Generati: {new Date(status.generatedAt).toLocaleDateString('it-IT')}
          </p>
        )}
        {status.lastUsedAt && (
          <p>
            Ultimo utilizzo: {new Date(status.lastUsedAt).toLocaleDateString('it-IT')}
          </p>
        )}
      </div>

      {/* Actions */}
      <button
        type="button"
        onClick={handleGenerate}
        disabled={loading}
        className="btn-secondary w-full"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Generazione...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            Genera nuovi codici
          </>
        )}
      </button>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  )
}
